import { useState, useEffect } from "react";
import { Mail, Check, Image as ImageIcon, Truck } from "lucide-react";
import { QuoteModal } from "./QuoteModal";
const B = process.env.REACT_APP_BACKEND_URL;

export function SummaryCard({ config, brand, selection, pricing, sku, isComplete }) {
  const [anim, setAnim] = useState(false);
  const [prev, setPrev] = useState(null);
  const [showQ, setShowQ] = useState(false);
  const isAerofoils = brand === "aerofoils";
  const steps = config?.steps || [];

  const pHt = pricing?.price_ht;
  const tva = pricing?.tva_amount;
  const pTtc = pricing?.price_ttc;

  useEffect(() => {
    if (pTtc !== prev && pTtc != null) { setAnim(true); setTimeout(() => setAnim(false), 300); setPrev(pTtc); }
  }, [pTtc, prev]);

  const pImg = () => {
    if (!isAerofoils || !selection.serie || !selection.couleur) return null;
    const u = config.product_images?.[`${selection.serie}_${selection.couleur}`];
    return u ? `${B}${u}` : null;
  };

  const getLabel = (stepId) => {
    const stepDef = steps.find(s => s.id === stepId);
    if (!stepDef) return null;
    const val = selection[stepId];
    if (!val) return null;

    if (isAerofoils) {
      if (stepId === "serie") return config.series?.find(s => s.id === val)?.name;
      if (stepId === "couleur") return config.colors?.find(c => c.id === val)?.name;
      if (stepId === "aile") return `${val} cm2`;
      if (stepId === "batterie") return config.batteries?.find(b => b.id === val)?.name;
      if (stepId === "chargeur") return config.chargers?.find(c => c.id === val)?.name;
    }

    // Dynamic board sub-steps
    const bt = (config.board_types || []).find(b => b.id === selection.board_type);
    if (stepId === "board_type") return (config.board_types || []).find(b => b.id === val)?.name;
    if (stepId === "board_model" && bt) return bt.models?.find(m => m.id === val)?.name;
    if (stepId === "board_color" && bt) return bt.colors?.find(c => c.id === val)?.name;

    const dataKey = stepDef.data_key || stepId;
    const options = config[dataKey] || [];
    const opt = options.find(o => o.id === val);
    return opt?.name || val;
  };

  const getPrice = (stepId) => {
    if (isAerofoils || config?.pricing_mode !== "additive") return null;
    const val = selection[stepId];
    if (!val) return null;

    // Board model carries the price
    if (stepId === "board_model") {
      const bt = (config.board_types || []).find(b => b.id === selection.board_type);
      if (bt) return bt.models?.find(m => m.id === val)?.price_ht;
      return null;
    }
    // Board type: if no board_model step, show board price from first model
    if (stepId === "board_type") {
      const hasBoardModelStep = steps.some(s => s.id === "board_model");
      if (!hasBoardModelStep) {
        const bt = (config.board_types || []).find(b => b.id === val);
        if (bt && bt.models?.length > 0) {
          const modelId = selection.board_model || bt.models[0].id;
          return bt.models.find(m => m.id === modelId)?.price_ht;
        }
      }
      return null;
    }
    // Board color: show price_delta if any
    if (stepId === "board_color") {
      const bt = (config.board_types || []).find(b => b.id === selection.board_type);
      if (bt) {
        const color = bt.colors?.find(c => c.id === val);
        if (color?.price_delta) return color.price_delta;
      }
      return null;
    }

    const stepDef = steps.find(s => s.id === stepId);
    if (!stepDef) return null;
    const dataKey = stepDef.data_key || stepId;
    const options = config[dataKey] || [];
    const opt = options.find(o => o.id === val);
    return opt?.price_ht;
  };

  const hex = () => {
    if (isAerofoils) return config.colors?.find(c => c.id === selection.couleur)?.hex;
    // Fliteboard board color
    const bt = (config.board_types || []).find(b => b.id === selection.board_type);
    if (bt) return bt.colors?.find(c => c.id === selection.board_color)?.hex;
    return null;
  };

  const prog = () => {
    let c = 0;
    steps.forEach(s => { if (selection[s.id]) c++; });
    return steps.length > 0 ? (c / steps.length) * 100 : 0;
  };

  const fp = p => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(p);

  return (
    <>
      <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6 shadow-lg" data-testid="summary-card">
        {pImg() ? (
          <div className="mb-4 -mx-6 -mt-6 overflow-hidden rounded-t-xl bg-[#0B0B0B]">
            <img src={pImg()} alt="Config" className="w-full h-56 object-contain p-3" />
          </div>
        ) : (isAerofoils && selection.serie && selection.couleur) && (
          <div className="mb-4 -mx-6 -mt-6 overflow-hidden rounded-t-xl bg-[#0B0B0B] h-32 flex items-center justify-center">
            <div className="text-center text-[#888]"><ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" /><span className="text-xs">Bientot disponible</span></div>
          </div>
        )}

        <div className="flex items-center gap-3 mb-4">
          <div className="relative">
            <div className="w-3 h-3 rounded-full bg-[#39FF14]"></div>
            <div className="absolute inset-0 w-3 h-3 rounded-full bg-[#39FF14] animate-ping opacity-30"></div>
          </div>
          <h3 className="text-lg font-bold text-white">Ma configuration</h3>
        </div>

        <div className="mb-6">
          <div className="h-1.5 bg-[#1A3A1A] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#2BC410] to-[#39FF14] progress-animate" style={{ width: `${prog()}%` }}></div>
          </div>
          <p className="text-xs text-[#888] mt-2">{Math.round(prog())}% complete</p>
        </div>

        <div className="space-y-3 mb-6">
          {steps.map(s => {
            const label = getLabel(s.id);
            const itemPrice = getPrice(s.id);
            return (
              <div key={s.id} className="flex items-center justify-between">
                <span className="text-sm text-[#888]">{s.label}</span>
                <div className="flex items-center gap-2">
                  {(s.id === "couleur" || s.id === "board_color") && hex() && (
                    <div className="w-4 h-4 rounded-full border border-[#1A3A1A]" style={{ backgroundColor: hex() }}></div>
                  )}
                  <span className={`text-sm font-medium ${label ? "text-white" : "text-[#888]"}`}>
                    {label || "\u2014"}
                  </span>
                  {itemPrice != null && (
                    <span className="text-xs text-[#39FF14] ml-1">{fp(itemPrice)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-[#1A3A1A] my-6"></div>

        {/* Price breakdown: HT + TVA + TTC */}
        <div className="mb-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#888] uppercase tracking-wider">Prix HT</span>
            <span className={`text-lg font-bold ${pHt != null ? "text-white" : "text-[#888]"}`} data-testid="summary-price-ht">
              {pHt != null ? fp(pHt) : "\u2014 \u20ac"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#888]">TVA 20%</span>
            <span className={`text-sm ${tva != null ? "text-[#888]" : "text-[#888]"}`} data-testid="summary-tva">
              {tva != null ? `+ ${fp(tva)}` : "\u2014"}
            </span>
          </div>
          <div className="border-t border-[#1A3A1A] pt-2 flex items-center justify-between">
            <span className="text-xs text-[#39FF14] uppercase tracking-wider font-bold">Prix TTC</span>
            <span className={`text-3xl font-black ${pTtc != null ? "text-[#39FF14]" : "text-[#888]"} ${anim ? "price-flash" : ""}`} data-testid="summary-price-ttc">
              {pTtc != null ? fp(pTtc) : "\u2014 \u20ac"}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <Truck className="w-3.5 h-3.5 text-[#888]" />
            <span className="text-xs text-[#888]">Hors frais de livraison</span>
          </div>
        </div>

        {sku && (
          <div className="mb-6 p-3 bg-[#0B0B0B] rounded-lg">
            <p className="text-xs text-[#888] uppercase mb-1">SKU</p>
            <p className="text-sm font-mono text-white" data-testid="summary-sku">{sku}</p>
          </div>
        )}

        {isComplete && (
          <div className="flex items-center gap-2 mb-6 p-3 rounded-lg badge-green">
            <Check className="w-5 h-5 text-[#39FF14]" />
            <span className="text-sm font-medium text-[#39FF14]">Complete</span>
          </div>
        )}

        <button
          onClick={() => setShowQ(true)}
          disabled={!isComplete}
          data-testid="request-quote-btn"
          className={`w-full flex items-center justify-center gap-2 px-4 py-4 rounded-lg font-bold text-lg transition-all ${isComplete ? "bg-[#39FF14] text-black hover:bg-[#2BC410] shadow-neon-strong" : "bg-[#1A3A1A] text-[#888] cursor-not-allowed"}`}
        >
          <Mail className="w-5 h-5" />Demander un devis
        </button>
        {!isComplete && <p className="text-xs text-[#888] text-center mt-3">Completez la configuration</p>}
      </div>
      <QuoteModal isOpen={showQ} onClose={() => setShowQ(false)} config={config} brand={brand} selection={selection} pricing={pricing} sku={sku} />
    </>
  );
}
